import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {View, FlatList, TouchableOpacity, Image} from 'react-native';
import searchIcon from '../../Assets/Images/png/search.png';
import NoIssueSelected from '../../Assets/Images/Svgs/no_issue_selected.svg';
import Box from '@/Atoms/Box';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation} from '@react-navigation/native';
import CCButton from '@/Atoms/CCButton';
import {ScreenName} from '@/navigators/StackConstants';
import {useGetUserTickets} from '@/Hooks/GetSupportModalApi/useGetUserTickets';
import {formatDateToDDMMYYYY, formatTimeHHMM} from '@/Utils/CommonUtils';
import {useGetTicketDetails} from '@/Hooks/GetSupportModalApi/useGetTicketDetails';
import {useSendTicketMessage} from '@/Hooks/GetSupportModalApi/useSendTicketMessage';
import {Video} from 'expo-av';
import ChatView, {Message} from '../SupportTicket/components/ChatView';
import ChatList from '../SupportTicket/components/ChatList';
import {styles} from '../SupportTicket/style';
import useGetInboxApi from '@/Hooks/GetInboxApi/useGetInboxApi';
import useGetMessagesApi from '@/Hooks/GetInboxApi/useGetChannelMessagesApi';
import useSendToChannelApi from '@/Hooks/GetInboxApi/useSendToChannelApi';
import useUploadedFileApi from '@/Hooks/GetInboxApi/useUploadedFileApi';
import {useNotificationStore} from '@/Store/useNotificationStore';
import {useSocket} from '@/Hooks/UseSocket';
import { useRoute } from '@react-navigation/native'


interface Ticket {
  id: string;
  title: string;
  date: string;
  time: string;
  status: 'Resolved' | 'In progress' | 'Not Seen';
  unreadCount?: number;
  messages: Message[];
  /** From get_inbox: true for LEAGUE or 2-user DIRECT (can send messages) */
  enable_inbox_send?: boolean;
}

const Inbox = ({dashboardData}) => {
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation();
  const flatListRef = useRef<FlatList>(null);
  const [search, setSearch] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const selectedTicketIdRef = useRef(selectedTicketId);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [visible, setVisible] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'status'>('status');
  const [message, setMessage] = useState('');
  const route = useRoute();
  const clearInboxMessage = useNotificationStore(
    state => state.clearInboxMessages,
  );
  const socket = useSocket();

  

  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId;
  }, [selectedTicketId]);

  const inboxMessageCallback = (socket, data) => {
    console.log('refetch messages on notification');
    refetchInboxDetails();
    if (selectedTicketIdRef.current) {
      console.log('asdf', selectedTicketIdRef.current);
      refetchForChannel(selectedTicketIdRef.current);
    }
  };

  useEffect(() => {
    socket?.registerMessageHandler('inbox_message', function (socket, data) {
      inboxMessageCallback(socket, data);
    });
  }, [socket]);

  const userKey = dashboardData?.user?.user_key;
  const handleOnSuccessInbox = data => {
    console.log(data, 'inbox messages data');
    refetchInboxDetails();
  };
  const handleOnErrorInbox = () => {};
  const {
    data: messagesData,
    isLoadingMessages,
    isErrorMessages,
    refetch: refetchMessageDetails,
    refetchForChannel,
  } = useGetMessagesApi(
    selectedTicketId,
    handleOnSuccessInbox,
    handleOnErrorInbox,
  );

  const handleOnSuccess = useCallback(
    data => {
      if (data.every(item => item.unread_count === 0)) {
        setTimeout(() => {
          clearInboxMessage();
        }, 100);
      }
    },
    [clearInboxMessage],
  );
  const handleOnError = () => {};
  const {
    data: inboxData = [],
    isLoading,
    isError,
    refetch: refetchInboxDetails,
  } = useGetInboxApi(handleOnSuccess, handleOnError);

  const {mutate: sendToChannel, isLoading: isSendLoading} =
    useSendToChannelApi(selectedTicketId ?? 0);
  const {mutateAsync: uploadFile, isLoading: isUploadLoading} =
    useUploadedFileApi();

  const fileUrlToMarkdown = (url: string): string => {
    const imageExtensions = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
    if (imageExtensions.test(url)) {
      return `![attachment](${url})`;
    }
    return `[View attachment](${url})`;
  };

  const handleSend = async () => {
    if (isSendLoading || isUploadLoading) return;
    if (!message?.trim() && !file) return;
    if (!selectedTicketId) return;

    let bodyText = message?.trim() ?? '';
    if (file) {
      try {
        const fileUrl = await uploadFile(file);
        setFile(null);
        setErrorMessage(null);
        const attachmentMarkdown = fileUrlToMarkdown(fileUrl);
        bodyText = bodyText ? `${bodyText}\n${attachmentMarkdown}` : attachmentMarkdown;
      } catch (err) {
        console.log('Failed to upload file', err);
        setErrorMessage(
          err instanceof Error ? err.message : 'Failed to upload file',
        );
        return;
      }
    }

    sendToChannel(
      {body: bodyText, title: ''},
      {
        onSuccess: () => {
          setMessage('');
          setTimeout(
            () =>
              flatListRef.current?.scrollToOffset({
                offset: 0,
                animated: true,
              }),
            100,
          );
        },
        onError: err => {
          console.log('Failed to send message', err);
        },
      },
    );
  };

  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      event.target.value = '';
      return;
    }

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    const isValidFileType =
      selectedFile.type.startsWith('image') ||
      selectedFile.type.startsWith('video');
    const isValidSize = fileSizeMB <= 15;

    if (!isValidFileType || !isValidSize) {
      setFile(null);
      setErrorMessage('Invalid file type or exceeds 15MB size limit.');
    } else {
      setFile(selectedFile);
      setErrorMessage(null);
      console.log('Selected file:', selectedFile);
    }

    event.target.value = '';
  };
  useEffect(() => {
    return () => {
      // Component unmounted ✅
      console.log('Unmounted');
      selectedTicketIdRef.current = null; // cleanup or notify unmount
    };
  }, []);
  const src = Array.isArray(inboxData) ? inboxData : [];

  const normalizedTickets = src.map(item => ({
    id: item.id,
    name: item.name,
    title: item.last_message?.title,
    status: item.is_active ? 'In progress' : 'Resolved',
    user_unread_count: item.unread_count,
    category: item.type,
    enable_inbox_send: item.enable_inbox_send ?? false,
    created_at: item.last_message?.created_at,
    last_updated: item.last_message_at,
    body: item.last_message?.body,
  }));

  const filteredTickets = normalizedTickets.filter(ticket =>
    ticket.name.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const { channelId } = route.params || {};
    console.log(channelId, normalizedTickets, 'inbox ')
    if (!channelId || !normalizedTickets.length) return;

    const ticket = normalizedTickets.find(
      t => String(t.id) == String(channelId),
    );

    if (ticket) {
      setSelectedTicketId(ticket.id);
      setSelectedTicket(ticket);
      if (route.params) {
        navigation.setParams({ ticketId: undefined, channelId: undefined });
      }
    }
  }, [route.params, normalizedTickets]);

  const handleSelect = (type: 'date' | 'status') => {
    setSortBy(type);
    setVisible(false);
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Image
        source={NoIssueSelected}
        alt="No issue selected"
        style={{width: 180, height: 180, marginBottom: 20}}
      />
      <CCText
        fontSize={FontSizes[16]}
        color={CCColors?.TextColor?.Dark}
        style={styles.emptyText}>
        No conversations selected
      </CCText>
      {/* <CCText
        fontSize={FontSizes[14]}
        color={CCColors?.TextColor?.Dark}
        style={styles.emptySubText}>
        Select a chat from the left list
      </CCText> */}
    </View>
  );
  const currentUsername = dashboardData?.user?.username;
  const messages = React.useMemo(() => {
    if (!messagesData?.messages) return [];

    return [...messagesData.messages] // copy first!
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ) // latest first
      .map(c => {
        const isCurrentUser = c.sender != null && c.sender === currentUsername;
        return {
          id: String(c.id),
          text: c.body,
          from: isCurrentUser ? ('right' as const) : ('left' as const),
          user: isCurrentUser ? 'You' : 'S',
          senderDisplayName: isCurrentUser ? undefined : (c.sender ?? 'Support'),
          uploaded_file: c.uploaded_file,
          created_at: c.created_at,
        };
      });
  }, [messagesData, currentUsername]);
  console.log(
    'Before sort:',
    messagesData?.messages?.map(m => m.created_at),
  );

  console.log(messages, 'messagesData');

  return (
    <View style={styles.container}>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={12}>
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap={12}>
          <TouchableOpacity
            onPress={
              isMobileOrTab
                ? selectedTicket
                  ? () => setSelectedTicket(null)
                  : () => navigation.goBack()
                : navigation.canGoBack()
                ? () => navigation.goBack()
                : () => navigation.navigate(ScreenName?.Dashboard)
            }>
            <MaterialCommunityIcons
              name="arrow-left"
              size={24}
              color={CCColors.Primary.Dark}
            />
          </TouchableOpacity>
          <CCText fontSize={FontSizes[20]}>Reminders</CCText>
        </Box>
      </Box>
      <View style={styles.innerContainer}>
        {isMobileOrTab ? (
          selectedTicket ? (
            <View style={styles.rightPanel}>
              <ChatView
                message={message}
                setMessage={setMessage}
                handleSend={handleSend}
                handleFileChange={handleFileChange}
                details={{
                  ...messagesData,
                  name: selectedTicket?.name,
                }}
                file={file}
                setFile={setFile}
                flatListRef={flatListRef}
                messages={messages}
                isTicketList={false}
                enableInboxSend={selectedTicket?.enable_inbox_send ?? false}
                showTime={true}
                refresh={false}
                handleRefresh={() => {
                  refetchMessageDetails();
                }}
                ticket={selectedTicket}
                errorMessage={errorMessage}
                showInboxSenderName={messagesData?.channel_type !== 'DIRECT'}
              />
            </View>
          ) : (
            <ChatList
              search={search}
              setSearch={setSearch}
              visible={visible}
              setVisible={setVisible}
              sortBy={sortBy}
              handleSelect={handleSelect}
              filteredTickets={filteredTickets}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              setSelectedTicketId={setSelectedTicketId}
              showSearchBar={false}
              showFilter={false}
            />
          )
        ) : (
          <>
            <ChatList
              search={search}
              setSearch={setSearch}
              visible={visible}
              setVisible={setVisible}
              sortBy={sortBy}
              handleSelect={handleSelect}
              filteredTickets={filteredTickets}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              setSelectedTicketId={setSelectedTicketId}
              showSearchBar={true}
              showFilter={false}
            />
            <View
              style={[
                styles.rightPanel,
                {
                  justifyContent:
                    selectedTicketId && messagesData ? 'flex-start' : 'center',
                  alignItems:
                    selectedTicketId && messagesData ? 'flex-start' : 'center',
                },
              ]}>
              {selectedTicketId && messagesData ? (
                <ChatView
                  message={message}
                  setMessage={setMessage}
                  handleSend={handleSend}
                  handleFileChange={handleFileChange}
                  details={{
                    ...messagesData,
                    name: selectedTicket?.name,
                  }}
                  file={file}
                  setFile={setFile}
                  flatListRef={flatListRef}
                  messages={messages}
                  isTicketList={false}
                  enableInboxSend={selectedTicket?.enable_inbox_send ?? false}
                  showTime={true}
                  refresh={false}
                  handleRefresh={() => {
                    refetchMessageDetails();
                  }}
                  ticket={selectedTicket}
                  errorMessage={errorMessage}
                  showInboxSenderName={messagesData?.channel_type !== 'DIRECT'}
                />
              ) : (
                renderEmptyState()
              )}
            </View>
          </>
        )}
      </View>
    </View>
  );
};

export default Inbox;
